import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Eye, Trash2, X, Search, ImageDown } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { useAuth } from "@/context/AuthContext";
import api, { fmtErr } from "@/lib/api";
import { fmtHeight, fmtMeasure } from "@/lib/units";
import { imgSrc } from "@/lib/imgSrc";
import { currentSeason } from "@/lib/season";
import { stripMarks } from "@/components/RichText";
import { DocumentChips } from "@/components/DocumentsManager";
import AdminCastingManager from "@/components/AdminCastingManager";
import AdminCategories from "@/components/AdminCategories";
import AdminEmailLog from "@/components/AdminEmailLog";
import AdminInbox from "@/components/AdminInbox";
import AdminAnnounce from "@/components/AdminAnnounce";
import AdminAnalytics from "@/components/AdminAnalytics";
import AdminAffiliates from "@/components/AdminAffiliates";
import { usePageMeta } from "@/lib/seo";

function OnOffToggle({ label, value, onChange, testPrefix }) {
  return (
    <div data-testid={`${testPrefix}-toggle`} className="flex flex-col gap-2">
      <p className="overline-label !text-[10px]">{label}</p>
      <div className="flex border border-white/20">
        {["on", "off"].map((m) => (
          <button key={m} data-testid={`${testPrefix}-${m}`} onClick={() => onChange(m)}
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors ${
              value === m ? "bg-white text-black" : "text-white/60 hover:text-white"}`}>
            {m}
          </button>
        ))}
      </div>
    </div>
  );
}


const TAB_KEYS = ["overview", "castings", "applications", "members", "affiliates", "inbox", "settings"];

export default function AdminDashboard() {
  usePageMeta({ title: "The Office | Solis Casting", noindex: true });
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [reports, setReports] = useState([]);
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [preview, setPreview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmAdmin, setConfirmAdmin] = useState(null);
  const [userQuery, setUserQuery] = useState("");
  const [memberPage, setMemberPage] = useState(0);
  const [appAction, setAppAction] = useState(null);
  const [appNote, setAppNote] = useState("");
  const [appFilter, setAppFilter] = useState("all");
  const [appQuery, setAppQuery] = useState("");
  const [confirmAppDelete, setConfirmAppDelete] = useState(null);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [emailMode, setEmailMode] = useState(null);
  const [emailTestBusy, setEmailTestBusy] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [stripMode, setStripMode] = useState(null);
  const [features, setFeatures] = useState(null);
  const [optimizing, setOptimizing] = useState(false);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/castings").then((r) => setPending(r.data)).catch(() => {});
    api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {});
    api.get("/admin/applications").then((r) => setApplications(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/settings/email").then((r) => setEmailMode(r.data)).catch(() => {});
    api.get("/admin/settings/homepage-strip").then((r) => setStripMode(r.data.mode)).catch(() => {});
    api.get("/homepage/hero").then((r) => setHeroes(r.data?.heroes || {})).catch(() => {});
    api.get("/auth/backdrop").then((r) => setBackdrop(r.data)).catch(() => {});
    api.get("/admin/settings/features").then((r) => setFeatures(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setMemberPage(0); }, [roleFilter, userQuery]);

  const changeStripMode = async (mode) => {
    try {
      await api.patch("/admin/settings/homepage-strip", { mode });
      setStripMode(mode);
      toast.success(mode === "on"
        ? "Homepage live counter is showing."
        : "Homepage live counter is hidden.");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const changeFeature = async (key, mode) => {
    try {
      await api.patch("/admin/settings/features", { key, mode });
      setFeatures((prev) => ({ ...prev, [key]: mode }));
      toast.success(mode === "on" ? "Switched on." : "Switched off.");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const optimizeImages = async () => {
    setOptimizing(true);
    try {
      let totalSaved = 0;
      let totalOptimized = 0;
      for (;;) {
        const { data } = await api.post("/admin/images/optimize");
        totalSaved += data.saved_kb;
        totalOptimized += data.optimized;
        if (!data.remaining) break;
      }
      toast.success(totalOptimized
        ? `Optimized ${totalOptimized} images — ${(totalSaved / 1024).toFixed(1)}MB lighter.`
        : "All images are already lean.");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setOptimizing(false);
    }
  };

  const changeEmailMode = async (mode) => {
    try {
      const r = await api.patch("/admin/settings/email", { mode });
      setEmailMode((prev) => ({ ...prev, mode, silenced: r.data.silenced }));
      toast.success(r.data.silenced
        ? "Outgoing email is silenced — sends are logged, not delivered."
        : "Outgoing email is live — sends will be delivered.");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const testEmail = async () => {
    setEmailTestBusy(true);
    try {
      const { data } = await api.post("/admin/settings/email/test");
      if (data.ok) {
        toast.success(`Delivery test accepted — check ${data.to} (and its spam folder).`);
      } else {
        toast.error(`Delivery test failed: ${data.error}`);
      }
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setEmailTestBusy(false);
    }
  };

  const review = async (id, action) => {
    try {
      await api.post(`/admin/castings/${id}/review`, { action });
      toast.success(action === "approve" ? "Casting published." : "Casting rejected.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const dismissReport = async (id) => {
    try {
      await api.patch(`/admin/reports/${id}`, { action: "dismiss" });
      toast.success("Report dismissed.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const takedownReported = async (report) => {
    try {
      await api.post(`/admin/castings/${report.casting_id}/unpublish`);
      await api.patch(`/admin/reports/${report.id}`, { action: "dismiss" });
      toast.success("Call taken down and report closed.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const PLAN_CHOICES = {
    model: [{ v: "trial", label: "Trial — 14 days" }, { v: "plan_talent", label: "Talent Pass (comp)" }],
    employer: [
      { v: "trial", label: "Trial — 14 days" },
      { v: "plan_starter", label: "Starter (comp)" },
      { v: "plan_pro", label: "Studio (comp)" },
      { v: "plan_unlimited", label: "Atelier (comp)" },
    ],
  };
  const [choices, setChoices] = useState({});

  const grant = async (u) => {
    const planKey = choices[u.id] || "trial";
    try {
      await api.post(`/admin/users/${u.id}/grant`, { plan_key: planKey });
      toast.success(`${PLAN_CHOICES[u.role].find((c) => c.v === planKey)?.label} granted to ${u.name}.`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const cancelPlan = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/cancel-plan`);
      toast.success(`${u.name}'s access cancelled.`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleHold = async (u) => {
    const holding = !u.plan?.on_hold;
    try {
      await api.post(`/admin/users/${u.id}/${holding ? "hold" : "unhold"}`, holding ? { reason: holdReason } : {});
      toast.success(holding
        ? `${u.name} is on hold — they've been emailed to update their payment.`
        : `${u.name}'s hold lifted.`);
      setHoldFor(null);
      setHoldReason("");
      setFunnelTick((t) => t + 1);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const [holdFor, setHoldFor] = useState(null);
  const [holdReason, setHoldReason] = useState("");
  const [funnelTick, setFunnelTick] = useState(0);
  const downloadBackup = async () => {
    try {
      const r = await api.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `solis-backup-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded — members, castings, applications and payments included.");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const openPreview = async (u) => {
    try {
      const r = await api.get(`/admin/users/${u.id}`);
      setPreview(r.data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const deleteUser = async (u) => {
    if (confirmDelete !== u.id) {
      setConfirmDelete(u.id);
      setTimeout(() => setConfirmDelete((c) => (c === u.id ? null : c)), 4000);
      return;
    }
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.name} deleted.`);
      setConfirmDelete(null);
      setPreview((p) => (p?.id === u.id ? null : p));
      load();
    } catch (e) {
      toast.error(fmtErr(e));
      setConfirmDelete(null);
    }
  };

  const setAppStatus = async (appId, status, note = "") => {
    try {
      await api.patch(`/admin/applications/${appId}`, { status, note: note || undefined });
      setApplications((prev) => prev.map((a) => (a.id === appId ? { ...a, status, response_note: note || a.response_note } : a)));
      setAppAction(null);
      setAppNote("");
      toast.success(status === "booked" ? "Booked — the model has been emailed." : `Marked ${status}.`);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleAdmin = async (u) => {
    if (confirmAdmin !== u.id) {
      setConfirmAdmin(u.id);
      setTimeout(() => setConfirmAdmin((c) => (c === u.id ? null : c)), 4000);
      return;
    }
    try {
      const r = await api.post(`/admin/users/${u.id}/toggle-admin`);
      toast.success(u.role === "admin" ? `${u.name} is back to ${r.data.role}.` : `${u.name} now holds office keys.`);
      setConfirmAdmin(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
      setConfirmAdmin(null);
    }
  };

  const deleteApplication = async (a) => {
    if (confirmAppDelete !== a.id) {
      setConfirmAppDelete(a.id);
      setTimeout(() => setConfirmAppDelete((c) => (c === a.id ? null : c)), 4000);
      return;
    }
    try {
      await api.delete(`/admin/applications/${a.id}`);
      setApplications((prev) => prev.filter((x) => x.id !== a.id));
      setConfirmAppDelete(null);
      toast.success("Application deleted.");
    } catch (e) {
      toast.error(fmtErr(e));
      setConfirmAppDelete(null);
    }
  };

  const purgeRejected = async () => {
    if (!confirmPurge) {
      setConfirmPurge(true);
      setTimeout(() => setConfirmPurge(false), 4000);
      return;
    }
    try {
      const { data } = await api.post("/admin/applications/purge-rejected");
      setApplications((prev) => prev.filter((x) => x.status !== "rejected"));
      setConfirmPurge(false);
      toast.success(`${data.deleted} rejected application${data.deleted === 1 ? "" : "s"} deleted.`);
    } catch (e) {
      toast.error(fmtErr(e));
      setConfirmPurge(false);
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TAB_KEYS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "overview";
  const setTab = (t) => {
    const p = new URLSearchParams(searchParams);
    p.set("tab", t);
    setSearchParams(p, { replace: true });
  };
  const tabCls = (t) => (tab === t ? "" : "hidden");
  const openReports = reports.filter((r) => r.status === "open").length;
  const TABS = [
    ["overview", "Overview", 0],
    ["castings", "Castings", pending.length + openReports],
    ["applications", "Applications", 0],
    ["members", "Members", 0],
    ["affiliates", "Affiliates", 0],
    ["inbox", "Inbox", unreadMsgs],
    ["settings", "Settings", 0],
  ];

  const uq = userQuery.trim().toLowerCase();
  const hasAccess = (u) => u.plan && (!u.plan.trial_ends || Date.parse(u.plan.trial_ends) > Date.now());
  const byRole = roleFilter === "all" ? users
    : roleFilter === "access" ? users.filter(hasAccess)
    : users.filter((u) => u.role === roleFilter);
  const filteredUsers = uq
    ? byRole.filter((u) => `${u.name} ${u.email} ${u.role} ${u.plan?.name || ""}`.toLowerCase().includes(uq))
    : byRole;
  const roleCounts = {
    all: users.length,
    model: users.filter((u) => u.role === "model").length,
    employer: users.filter((u) => u.role === "employer").length,
    access: users.filter(hasAccess).length,
  };
  const MEMBERS_PAGE_SIZE = 50;
  const memberPages = Math.max(1, Math.ceil(filteredUsers.length / MEMBERS_PAGE_SIZE));
  const safeMemberPage = Math.min(memberPage, memberPages - 1);
  const pagedUsers = filteredUsers.slice(safeMemberPage * MEMBERS_PAGE_SIZE, (safeMemberPage + 1) * MEMBERS_PAGE_SIZE);
  const aq = appQuery.trim().toLowerCase();
  const filteredApps = applications
    .filter((a) => appFilter === "all" || a.status === appFilter)
    .filter((a) => !aq || [a.model_name, a.casting_title, a.brand].some((v) => (v || "").toLowerCase().includes(aq)));

  /* One-click CSV export of the members table — exports the current view
     (active tab + search), so "Full Access" gives a paying-members-only file. */
  const exportMembersCsv = () => {
    if (!filteredUsers.length) { toast.error("No members in the current view to export."); return; }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [
      ["Name", "Email", "Role", "Company", "Plan", "Plan type", "Trial ends", "Stripe-backed", "Joined"],
      ...filteredUsers.map((u) => [
        u.name, u.email, u.role, u.company || "",
        u.plan?.name || "", u.plan?.key || "",
        u.plan?.trial_ends ? new Date(u.plan.trial_ends).toLocaleDateString("en-CA") : "",
        u.plan ? (u.plan.stripe ? "yes" : "no (comp)") : "",
        u.created_at ? new Date(u.created_at).toLocaleDateString("en-CA") : "",
      ]),
    ];
    const csv = rows.map((r) => r.map(esc).join(",")).join("\r\n");
    const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `solis-members-${roleFilter}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${filteredUsers.length} members to CSV.`);
  };

  const [heroes, setHeroes] = useState(null);
  const [heroSeason, setHeroSeason] = useState("default");
  const [heroBusy, setHeroBusy] = useState(false);
  const heroFileRef = useRef(null);
  const [backdrop, setBackdrop] = useState(null);
  const [backdropBusy, setBackdropBusy] = useState(false);
  const backdropFileRef = useRef(null);

  const uploadBackdrop = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBackdropBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/admin/uploads/media", fd);
      await api.patch("/admin/settings/auth-backdrop", { url: up.url, kind: up.kind });
      setBackdrop({ url: up.url, kind: up.kind });
      toast.success(`${up.kind === "video" ? "Video" : "Image"} backdrop is live on the sign-in page.`);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBackdropBusy(false);
    }
  };

  const removeBackdrop = async () => {
    try {
      await api.patch("/admin/settings/auth-backdrop", { url: "", kind: "image" });
      setBackdrop({ url: null, kind: null });
      toast.success("Backdrop removed — the sign-in page is clean again.");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const uploadHero = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setHeroBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/uploads/image", fd);
      await api.patch("/admin/settings/homepage-hero", { hero_url: up.url, season: heroSeason });
      setHeroes((h) => ({ ...(h || {}), [heroSeason]: up.url }));
      toast.success(heroSeason === "default"
        ? "Homepage hero updated — live for every visitor now."
        : `${heroSeason === "halloween" ? "Halloween" : "Christmas"} hero set — it takes over automatically in season.`);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setHeroBusy(false);
    }
  };

  return (
    <div className="px-6 sm:px-10 py-24" data-testid="admin-dashboard">
      <Reveal>
        <p className="overline-label mb-4">Backstage</p>
        <div className="mb-10">
          <h1 className="font-display font-light text-4xl sm:text-6xl tracking-tighter">
            The <span className="italic text-accent">Office</span>
          </h1>
        </div>
      </Reveal>

      <div className="sticky top-16 z-30 bg-background border-b border-white/10 mb-12 -mx-6 px-6 sm:-mx-10 sm:px-10 overflow-x-auto" data-testid="admin-tabs">
        <div className="flex min-w-max">
          {TABS.map(([key, label, count]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`font-grotesk text-[11px] uppercase tracking-[0.18em] px-4 sm:px-6 py-4 border-b-2 transition-colors whitespace-nowrap ${tab === key ? "border-accent text-white" : "border-transparent text-white/45 hover:text-white"}`}
            >
              {label}
              {count > 0 && <span className="ml-2 text-accent" data-testid={`admin-tab-count-${key}`}>{count}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className={tabCls("settings")} data-testid="admin-tabpanel-settings">
      <div className="flex flex-wrap items-center gap-6 mb-16 border border-white/10 p-6" data-testid="admin-quick-controls">
            {stripMode && (
              <OnOffToggle label="Homepage live counter" value={stripMode} onChange={changeStripMode} testPrefix="strip-mode" />
            )}
            {features && (
              <>
                <OnOffToggle label="Success stories strip" value={features.success_strip} onChange={(m) => changeFeature("success_strip", m)} testPrefix="feature-success" />
                <OnOffToggle label="Report button" value={features.report_button} onChange={(m) => changeFeature("report_button", m)} testPrefix="feature-report" />
                <OnOffToggle label="Instagram popup" value={features.instagram_popup} onChange={(m) => changeFeature("instagram_popup", m)} testPrefix="feature-popup" />
              </>
            )}
            {emailMode && (
              <div data-testid="admin-email-mode" className="flex flex-col gap-2">
                <p className="overline-label !text-[10px]">
                  Outgoing email — <span className={emailMode.silenced ? "text-accent" : "text-white"}>
                    {emailMode.silenced ? "silenced" : "live"}</span>
                </p>
                <div className="flex border border-white/20">
                  {["auto", "on", "off"].map((m) => (
                    <button key={m} data-testid={`email-mode-${m}`} onClick={() => changeEmailMode(m)}
                      className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors ${
                        emailMode.mode === m ? "bg-white text-black" : "text-white/60 hover:text-white"}`}>
                      {m}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-white/40 max-w-[220px]">
                  {emailMode.mode === "auto"
                    ? "Auto — silenced on preview, live in production."
                    : emailMode.mode === "on"
                      ? "Forced on — every send is delivered, even on preview."
                      : "Forced off — nothing is delivered, anywhere."}
                </p>
                <button
                  data-testid="email-test-button"
                  onClick={testEmail}
                  disabled={emailTestBusy}
                  className="btn-outline !px-4 !py-2 !text-[10px] w-fit disabled:opacity-40"
                >
                  {emailTestBusy ? "Sending…" : "Send test email"}
                </button>
                {emailMode.key_configured === false && (
                  <p className="text-[10px] text-accent max-w-[220px]">Email key missing in this environment.</p>
                )}
              </div>
            )}
            <button data-testid="admin-optimize-images" onClick={optimizeImages} disabled={optimizing} className="btn-outline !px-5 !py-3 flex items-center gap-2 disabled:opacity-40">
              <ImageDown size={14} /> {optimizing ? "Optimizing…" : "Optimize images"}
            </button>
            <button data-testid="admin-backup-button" onClick={downloadBackup} className="btn-outline !px-5 !py-3 flex items-center gap-2">
              <Download size={14} /> Download backup
            </button>
      </div>

      {heroes && (
        <div className="flex flex-wrap items-center gap-6 mb-16 border border-white/10 p-6" data-testid="admin-hero-rotator">
          <img src={imgSrc(heroes[heroSeason] || heroes.default || "/hero-duo.jpg")} alt="Homepage hero preview" className="h-24 w-20 object-cover border border-white/15" data-testid="hero-current-preview" />
          <div className="flex-1 min-w-[220px]">
            <p className="overline-label !text-[10px] mb-1">Homepage hero</p>
            <p className="text-white/50 text-xs mb-3">The full-bleed photo every visitor meets first — swap it any time, no deploy needed. Seasonal slots take over automatically while their season runs.</p>
            <div className="flex border border-white/20 w-fit" data-testid="hero-season-picker">
              {[["default", "Year-round"], ["halloween", "Halloween"], ["christmas", "Christmas"]].map(([v, label]) => (
                <button key={v} data-testid={`hero-season-${v}`} onClick={() => setHeroSeason(v)}
                  className={`px-4 py-2 text-[10px] uppercase tracking-[0.15em] transition-colors ${heroSeason === v ? "bg-white text-black" : "text-white/60 hover:text-white"}`}>
                  {label}{currentSeason() === v ? " · live" : ""}
                </button>
              ))}
            </div>
          </div>
          <button data-testid="hero-upload-button" onClick={() => heroFileRef.current?.click()} disabled={heroBusy} className="btn-outline !px-5 !py-3 flex items-center gap-2 disabled:opacity-40">
            <ImageDown size={14} /> {heroBusy ? "Uploading…" : `Swap ${heroSeason === "default" ? "hero" : heroSeason + " hero"}`}
          </button>
          <input ref={heroFileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadHero} data-testid="hero-upload-file" />
        </div>
      )}

      {backdrop !== null && (
        <div className="flex flex-wrap items-center gap-6 mb-16 border border-white/10 p-6" data-testid="admin-backdrop-card">
          {backdrop?.url ? (
            backdrop.kind === "video" ? (
              <video src={imgSrc(backdrop.url)} className="h-24 w-20 object-cover border border-white/15" muted loop autoPlay playsInline data-testid="backdrop-preview-video" />
            ) : (
              <img src={imgSrc(backdrop.url)} alt="Sign-in backdrop" className="h-24 w-20 object-cover border border-white/15" data-testid="backdrop-preview-image" />
            )
          ) : (
            <div className="h-24 w-20 border border-dashed border-white/20 flex items-center justify-center text-white/30 text-[10px] uppercase tracking-[0.1em] text-center px-2" data-testid="backdrop-preview-empty">None</div>
          )}
          <div className="flex-1 min-w-[220px]">
            <p className="overline-label !text-[10px] mb-1">Sign-in backdrop</p>
            <p className="text-white/50 text-xs">
              {backdrop?.url
                ? `${backdrop.kind === "video" ? "Video" : "Image"} playing behind the sign-in page at 25% opacity.`
                : "Drop a moody runway video or editorial image behind the sign-in page — it plays at 25% opacity under a dark scrim."}
            </p>
          </div>
          <div className="flex gap-3">
            <button data-testid="backdrop-upload-button" onClick={() => backdropFileRef.current?.click()} disabled={backdropBusy} className="btn-outline !px-5 !py-3 flex items-center gap-2 disabled:opacity-40">
              <ImageDown size={14} /> {backdropBusy ? "Uploading…" : backdrop?.url ? "Replace" : "Add photo or video"}
            </button>
            {backdrop?.url && (
              <button data-testid="backdrop-remove-button" onClick={removeBackdrop} className="btn-outline !px-4 !py-3 text-white/50 hover:text-accent">Remove</button>
            )}
          </div>
          <input ref={backdropFileRef} type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime" className="hidden" onChange={uploadBackdrop} data-testid="backdrop-upload-file" />
        </div>
      )}

      <AdminCategories />

      <AdminEmailLog />
      </div>

      <div className={tabCls("overview")} data-testid="admin-tabpanel-overview">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10 border border-white/10 mb-16" data-testid="admin-stats">
          {[
            [stats.models, "Models"],
            [stats.employers, "Employers"],
            [stats.castings_pending, "In review"],
            [stats.castings_published, "Live calls"],
            [stats.applications, "Applications"],
            [`$${(stats.revenue_cents / 100).toFixed(0)}`, "Revenue"],
          ].map(([n, label]) => (
            <div key={label} className="bg-background p-6">
              <p className="font-display text-4xl mb-2">{n}</p>
              <p className="overline-label !text-[10px]">{label}</p>
            </div>
          ))}
        </div>
      )}

      <AdminAnalytics />

      <TrialFunnel tick={funnelTick} />
      </div>

      <div className={tabCls("affiliates")} data-testid="admin-tabpanel-affiliates">
      <AdminAffiliates />
      </div>

      <div className={tabCls("castings")} data-testid="admin-tabpanel-castings">
      <AdminCastingManager />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <section data-testid="admin-pending-castings">          <p className="overline-label mb-8">Castings awaiting review ({pending.length})</p>
          {pending.length === 0 ? (
            <p className="font-display italic text-2xl text-white/40">Nothing in the queue.</p>
          ) : (
            <div className="flex flex-col gap-4">
              {pending.map((c) => (
                <div key={c.id} className="panel p-6" data-testid={`pending-casting-${c.id}`}>
                  <p className="font-display text-2xl mb-1">{c.title}</p>
                  <p className="overline-label !text-[10px] mb-4">{c.brand} · {c.city} · by {c.employer_name}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6 line-clamp-2">{stripMarks(c.description)}</p>
                  <div className="flex gap-2">
                    <button data-testid={`approve-${c.id}`} onClick={() => review(c.id, "approve")} className="btn-primary !px-5 !py-2.5">Approve</button>
                    <button data-testid={`reject-${c.id}`} onClick={() => review(c.id, "reject")} className="btn-outline !px-5 !py-2.5">Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {reports.filter((r) => r.status === "open").length > 0 && (
          <section data-testid="admin-reports">
            <p className="overline-label mb-8 !text-accent">Reported calls ({reports.filter((r) => r.status === "open").length})</p>
            <div className="flex flex-col gap-4">
              {reports.filter((r) => r.status === "open").map((r) => (
                <div key={r.id} className="panel p-6" data-testid={`report-${r.id}`}>
                  <p className="font-display text-2xl mb-1">{r.casting_title}</p>
                  <p className="overline-label !text-[10px] mb-4">
                    {r.brand} · flagged by {r.reporter_name} ({r.reporter_email}) · {r.created_at?.slice(0, 10)}
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6 italic">“{r.reason}”</p>
                  <div className="flex flex-wrap gap-2">
                    <a href={`/castings/${r.casting_id}`} target="_blank" rel="noreferrer" data-testid={`report-view-${r.id}`} className="btn-outline !px-5 !py-2.5 inline-block">View call</a>
                    <button data-testid={`report-takedown-${r.id}`} onClick={() => takedownReported(r)} className="btn-primary !px-5 !py-2.5">Take call down</button>
                    <button data-testid={`report-dismiss-${r.id}`} onClick={() => dismissReport(r.id)} className="btn-outline !px-5 !py-2.5">Dismiss</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

      </div>
      </div>

      <div className={tabCls("applications")} data-testid="admin-tabpanel-applications">
        <section data-testid="admin-applications">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <p className="overline-label">All applications ({filteredApps.length}{appFilter !== "all" || aq ? ` of ${applications.length}` : ""})</p>
            <div className="relative w-full sm:w-72">
              <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
              <input
                data-testid="app-search"
                value={appQuery}
                onChange={(e) => setAppQuery(e.target.value)}
                placeholder="Search who applied — name, casting…"
                className="field !pl-10 !py-2.5 w-full"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {["all", "pending", "shortlisted", "booked", "rejected"].map((f) => (
                <button
                  key={f}
                  data-testid={`app-filter-${f}`}
                  onClick={() => setAppFilter(f)}
                  className={`font-grotesk text-[10px] uppercase tracking-[0.15em] border px-3 py-2 transition-colors ${appFilter === f ? "border-accent bg-accent text-white" : "border-white/25 text-white/60 hover:border-accent hover:text-accent"}`}
                >
                  {f}
                </button>
              ))}
              {applications.some((a) => a.status === "rejected") && (
                <button
                  data-testid="app-purge-rejected"
                  onClick={purgeRejected}
                  title={confirmPurge ? "Click again to confirm — this permanently deletes every rejected application" : "Delete every rejected application"}
                  className={`font-grotesk text-[10px] uppercase tracking-[0.15em] border px-3 py-2 transition-colors inline-flex items-center gap-2 ${confirmPurge ? "border-accent bg-accent text-white" : "border-white/25 text-white/60 hover:border-accent hover:text-accent"}`}
                >
                  <Trash2 size={12} />
                  {confirmPurge ? `Sure? Deletes ${applications.filter((a) => a.status === "rejected").length}` : `Delete all rejected (${applications.filter((a) => a.status === "rejected").length})`}
                </button>
              )}
            </div>
          </div>
          <div className="panel">
            {filteredApps.length === 0 ? (
              <p className="p-6 font-display italic text-2xl text-white/40">{aq ? "No applications match that name." : appFilter === "all" ? "No applications yet." : "Nothing with this status."}</p>
            ) : (
            filteredApps.map((a) => (
              <div key={a.id} className="px-6 py-4 border-b border-white/10 last:border-0 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4" data-testid={`admin-application-${a.id}`}>
                <div className="min-w-0">
                  <p className="text-sm">
                    <Link to={`/models/${a.model_id}`} target="_blank" data-testid={`admin-view-book-${a.id}`} className="hover:text-accent transition-colors">{a.model_name}</Link>
                    {" "}<span className="text-white/40">→</span> {a.casting_title}
                  </p>
                  <p className="overline-label !text-[10px] mt-1">{a.brand}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                  {appAction?.id === a.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        data-testid={`admin-note-${a.id}`}
                        value={appNote}
                        onChange={(e) => setAppNote(e.target.value)}
                        placeholder="A note for the model (optional)"
                        className="field !py-2 !text-xs w-56"
                      />
                      <button data-testid={`admin-note-send-${a.id}`} onClick={() => setAppStatus(a.id, appAction.status, appNote)} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-accent text-accent hover:bg-accent hover:text-white px-3 py-2 transition-colors">
                        Send
                      </button>
                      <button onClick={() => { setAppAction(null); setAppNote(""); }} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 text-white/50 hover:text-white px-3 py-2 transition-colors">
                        Back
                      </button>
                    </div>
                  ) : (
                    <>
                      {a.status === "booked" && (
                        <span className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-accent bg-accent text-white px-3 py-1.5">Booked</span>
                      )}
                      {a.status === "shortlisted" && (
                        <span className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-accent text-accent px-3 py-1.5">Shortlisted</span>
                      )}
                      {a.status === "rejected" && (
                        <span className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/10 text-white/30 px-3 py-1.5">Rejected</span>
                      )}
                      {a.status !== "shortlisted" && a.status !== "booked" && a.status !== "rejected" && (
                        <button data-testid={`admin-shortlist-${a.id}`} onClick={() => { setAppAction({ id: a.id, status: "shortlisted" }); setAppNote(""); }} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 hover:border-accent hover:text-accent px-3 py-1.5 transition-colors">
                          Shortlist
                        </button>
                      )}
                      {a.status !== "booked" && a.status !== "rejected" && (
                        <button data-testid={`admin-book-${a.id}`} onClick={() => { setAppAction({ id: a.id, status: "booked" }); setAppNote(""); }} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-accent text-accent hover:bg-accent hover:text-white px-3 py-1.5 transition-colors">
                          Book
                        </button>
                      )}
                      {(a.status === "pending" || a.status === "reviewed") && (
                        <button data-testid={`admin-reject-${a.id}`} onClick={() => setAppStatus(a.id, "rejected")} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 hover:border-white hover:text-white px-3 py-1.5 transition-colors">
                          Pass
                        </button>
                      )}
                      <button
                        data-testid={`admin-delete-app-${a.id}`}
                        onClick={() => deleteApplication(a)}
                        title={confirmAppDelete === a.id ? "Click again to confirm deletion" : "Delete application"}
                        className={`font-grotesk text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors ${confirmAppDelete === a.id ? "border-accent bg-accent text-white" : "border-white/25 hover:border-accent hover:text-accent"}`}
                      >
                        {confirmAppDelete === a.id ? "Sure?" : <Trash2 size={13} />}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )))}
          </div>
        </section>
      </div>

      <div className={tabCls("inbox")} data-testid="admin-tabpanel-inbox">
      <AdminInbox onUnread={setUnreadMsgs} />
      <AdminAnnounce />
      </div>

      <div className={tabCls("members")} data-testid="admin-tabpanel-members">
      <section className="mt-16" data-testid="admin-users">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex flex-wrap items-center gap-6">
            <p className="overline-label !font-semibold !text-white/85">Members ({filteredUsers.length}{userQuery || roleFilter !== "all" ? ` of ${users.length}` : ""})</p>
            <div className="flex flex-wrap border border-white/20" data-testid="members-role-filter">
              {[["all", "All"], ["model", "Talent"], ["employer", "Brands"], ["access", "Full Access"]].map(([v, label]) => (
                <button key={v} data-testid={`members-filter-${v}`} onClick={() => setRoleFilter(v)}
                  className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] whitespace-nowrap transition-colors ${
                    roleFilter === v ? "bg-white text-black" : "text-white/60 hover:text-white"}`}>
                  {label} ({roleCounts[v]})
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
            <button
              data-testid="members-export-csv"
              onClick={exportMembersCsv}
              title="Download the current members view as CSV"
              className="overline-label !text-[10px] inline-flex items-center justify-center gap-2 border border-white/20 hover:border-accent hover:text-accent px-4 py-2.5 transition-colors"
            >
              <Download size={13} /> Export CSV
            </button>
            <div className="relative w-full sm:w-72">
              <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
              <input
                data-testid="admin-users-search"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                placeholder="Search members — name, email…"
                className="field !pl-10 w-full"
              />
            </div>
          </div>
        </div>
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                {["Name", "Email", "Role", "Access", "Actions"].map((h) => (
                  <th key={h} className="text-left px-6 py-4 overline-label !text-[10px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedUsers.map((u) => (
                <tr key={u.id} className="border-b border-white/5 last:border-0" data-testid={`admin-user-${u.id}`}>
                  <td className="px-6 py-4 font-display text-lg">{u.name}</td>
                  <td className="px-6 py-4 text-muted-foreground">{u.email}</td>
                  <td className="px-6 py-4">
                    <span className={`font-grotesk text-[10px] uppercase tracking-[0.15em] ${u.role === "admin" ? "text-accent" : "text-white/60"}`}>{u.role}</span>
                  </td>
                  <td className="px-6 py-4">
                    {u.plan ? (
                      <span className={`font-grotesk text-[10px] uppercase tracking-[0.15em] ${u.plan.comp || u.plan.key === "trial" || u.plan.on_hold ? "text-accent" : "text-white"}`} data-testid={`plan-badge-${u.id}`}>
                        {u.plan.name}{u.plan.trial_ends ? ` · until ${new Date(u.plan.trial_ends).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}{u.plan.on_hold ? " · on hold" : ""}
                      </span>
                    ) : (
                      <span className="font-grotesk text-[10px] uppercase tracking-[0.15em] text-white/30">None</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {u.id !== user?.id && (
                      <div className="flex items-center gap-2">
                        {u.role !== "admin" && (
                          <>
                            <button data-testid={`preview-${u.id}`} onClick={() => openPreview(u)} title="Preview profile" className="border border-white/25 hover:border-accent hover:text-accent p-2 transition-colors">
                              <Eye size={13} />
                            </button>
                            <div className="relative">
                              <select
                                data-testid={`grant-select-${u.id}`}
                                value={choices[u.id] || "trial"}
                                onChange={(e) => setChoices({ ...choices, [u.id]: e.target.value })}
                                className="appearance-none bg-background border border-white/20 hover:border-white/60 transition-colors font-grotesk text-[10px] uppercase tracking-[0.15em] pl-3 pr-8 py-2 outline-none cursor-pointer"
                              >
                                {(PLAN_CHOICES[u.role] || []).map((c) => (
                                  <option key={c.v} value={c.v}>{c.label}</option>
                                ))}
                              </select>
                            </div>
                            <button data-testid={`grant-${u.id}`} onClick={() => grant(u)} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 hover:border-accent hover:text-accent px-3 py-2 transition-colors">
                              Grant
                            </button>
                            {u.plan && (u.plan.on_hold ? (
                              <button
                                data-testid={`hold-${u.id}`}
                                onClick={() => toggleHold(u)}
                                title="Lift the hold — restore full access"
                                className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-accent bg-accent text-white hover:bg-accent/80 px-3 py-2 transition-colors"
                              >
                                Lift hold
                              </button>
                            ) : holdFor === u.id ? (
                              <span className="flex items-center gap-2">
                                <input
                                  data-testid={`hold-reason-${u.id}`}
                                  value={holdReason}
                                  onChange={(e) => setHoldReason(e.target.value)}
                                  placeholder="Reason (optional, shown to member)"
                                  className="field !py-2 !text-[11px] !w-48"
                                />
                                <button data-testid={`hold-confirm-${u.id}`} onClick={() => toggleHold(u)} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-accent text-accent hover:bg-accent hover:text-white px-3 py-2 transition-colors">
                                  Set hold
                                </button>
                                <button data-testid={`hold-back-${u.id}`} onClick={() => { setHoldFor(null); setHoldReason(""); }} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 text-white/50 hover:text-white px-3 py-2 transition-colors">
                                  Back
                                </button>
                              </span>
                            ) : (
                              <button
                                data-testid={`hold-${u.id}`}
                                onClick={() => setHoldFor(u.id)}
                                title="Put on hold — blocks publishing/applying until lifted"
                                className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 hover:border-accent hover:text-accent px-3 py-2 transition-colors"
                              >
                                Hold
                              </button>
                            ))}
                            {u.plan && (
                              <button data-testid={`cancel-plan-${u.id}`} onClick={() => cancelPlan(u)} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 hover:border-accent hover:text-accent px-3 py-2 transition-colors">
                                Cancel
                              </button>
                            )}
                            <button
                              data-testid={`delete-${u.id}`}
                              onClick={() => deleteUser(u)}
                              title={confirmDelete === u.id ? "Click again to confirm deletion" : "Delete user"}
                              className={`font-grotesk text-[10px] uppercase tracking-[0.15em] border px-3 py-2 transition-colors ${confirmDelete === u.id ? "border-accent bg-accent text-white" : "border-white/25 hover:border-accent hover:text-accent"}`}
                            >
                              {confirmDelete === u.id ? "Sure?" : <Trash2 size={13} />}
                            </button>
                          </>
                        )}
                        <button
                          data-testid={`toggle-admin-${u.id}`}
                          onClick={() => toggleAdmin(u)}
                          title={u.role === "admin" ? "Revoke admin access" : "Make this member an admin"}
                          className={`font-grotesk text-[10px] uppercase tracking-[0.15em] border px-3 py-2 transition-colors ${confirmAdmin === u.id ? "border-accent bg-accent text-white" : "border-white/25 hover:border-accent hover:text-accent"}`}
                        >
                          {confirmAdmin === u.id ? "Sure?" : u.role === "admin" ? "Revoke" : "Make admin"}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {memberPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-4 mt-6" data-testid="members-pagination">
              <p className="font-grotesk text-[10px] uppercase tracking-[0.15em] text-white/50" data-testid="members-page-info">
                Showing {safeMemberPage * MEMBERS_PAGE_SIZE + 1}–{Math.min((safeMemberPage + 1) * MEMBERS_PAGE_SIZE, filteredUsers.length)} of {filteredUsers.length} members
              </p>
              <div className="flex border border-white/20">
                <button
                  data-testid="members-prev-page"
                  onClick={() => setMemberPage(safeMemberPage - 1)}
                  disabled={safeMemberPage === 0}
                  className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors text-white/60 hover:text-white disabled:opacity-30"
                >
                  Prev
                </button>
                {Array.from({ length: memberPages }, (_, p) => (
                  <button
                    key={p}
                    data-testid={`members-page-${p + 1}`}
                    onClick={() => setMemberPage(p)}
                    className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors ${safeMemberPage === p ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
                  >
                    {p + 1}
                  </button>
                ))}
                <button
                  data-testid="members-next-page"
                  onClick={() => setMemberPage(safeMemberPage + 1)}
                  disabled={safeMemberPage >= memberPages - 1}
                  className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors text-white/60 hover:text-white disabled:opacity-30"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
      </div>

      {preview && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" data-testid="user-preview-modal" onClick={() => setPreview(null)}>
          <div className="panel max-w-2xl w-full max-h-[85vh] overflow-y-auto p-8" data-lenis-prevent onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-6 mb-8">
              <div>
                <p className="overline-label !text-[10px] mb-2">{preview.role}</p>
                <h3 className="font-display font-light text-3xl tracking-tight" data-testid="preview-name">{preview.name}</h3>
                <p className="font-grotesk text-xs text-white/50 mt-1">{preview.email}</p>
              </div>
              <button data-testid="preview-close" onClick={() => setPreview(null)} className="btn-outline !px-3 !py-2"><X size={14} /></button>
            </div>

            <div className="flex flex-wrap gap-x-8 gap-y-2 mb-8">
              <span className="font-grotesk text-[10px] uppercase tracking-[0.15em] text-white/60">
                Joined {preview.created_at ? new Date(preview.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
              </span>
              <span className="font-grotesk text-[10px] uppercase tracking-[0.15em] text-white/60">Plan: {preview.plan?.name || "None"}</span>
              {preview.role === "model" && (
                <span className="font-grotesk text-[10px] uppercase tracking-[0.15em] text-white/60">Applications: {preview.applications_count}</span>
              )}
            </div>

            {preview.role === "model" && (
              <div className="space-y-6" data-testid="preview-model-profile">
                <p className="font-grotesk text-xs uppercase tracking-[0.15em] text-white/70">
                  {[preview.profile?.city, fmtHeight(preview.profile?.height_cm || preview.profile?.height), preview.profile?.bust && `Bust ${fmtMeasure(preview.profile.bust)}`, preview.profile?.waist && `Waist ${fmtMeasure(preview.profile.waist)}`, preview.profile?.hips && `Hips ${fmtMeasure(preview.profile.hips)}`, preview.profile?.hair, preview.profile?.eyes].filter(Boolean).join(" · ") || "No measurements yet"}
                </p>
                {(preview.profile?.categories || []).length > 0 && (
                  <p className="font-grotesk text-xs uppercase tracking-[0.15em] text-accent">{(preview.profile?.categories || []).join(" · ")}</p>
                )}
                {preview.profile?.bio && (
                  <p className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap">{stripMarks(preview.profile.bio)}</p>
                )}
                {(preview.profile?.photos || []).length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {(preview.profile?.photos || []).slice(0, 4).map((ph, i) => (
                      <img key={i} src={imgSrc(ph)} alt="" className="aspect-[3/4] w-full object-cover" data-testid={`preview-photo-${i}`} />
                    ))}
                  </div>
                )}
                <p className="font-grotesk text-[10px] uppercase tracking-[0.15em] text-white/50">
                  {(preview.profile?.photos || []).length} photos · {(preview.profile?.reels || []).length} reels · {(preview.profile?.work_history || []).length} credits
                </p>
                <a href={`/models/${preview.slug || preview.id}`} target="_blank" rel="noreferrer" data-testid="preview-public-link" className="btn-outline !px-5 !py-2.5 inline-block">
                  Open public profile
                </a>
              </div>
            )}

            {preview.role === "employer" && (
              <div className="space-y-6" data-testid="preview-employer-profile">
                {preview.company && <p className="font-grotesk text-xs uppercase tracking-[0.15em] text-white/70">{preview.company}</p>}
                <div>
                  <p className="overline-label !text-[10px] mb-3">Their castings ({(preview.castings || []).length})</p>
                  {(preview.castings || []).length === 0 ? (
                    <p className="font-display italic text-xl text-white/40">No postings yet.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {(preview.castings || []).map((c) => (
                        <div key={c.id} className="flex items-center justify-between gap-4 border-b border-white/10 pb-2">
                          <span className="font-display text-lg">{c.title}</span>
                          <span className="font-grotesk text-[10px] uppercase tracking-[0.15em] text-white/50">{c.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {(preview.documents || []).length > 0 && (
              <div className="mt-8" data-testid="preview-documents">
                <p className="overline-label !text-[10px] mb-3">Documents ({preview.documents.length})</p>
                <DocumentChips docs={preview.documents} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TrialFunnel({ tick = 0 }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/trials").then((r) => setData(r.data)).catch(() => {}); }, [tick]);
  if (!data) return null;
  const groups = [
    ["In trial", data.in_trial, "in-trial"],
    ["Converted to paid", data.converted, "converted"],
    ["Lapsed without converting", data.lapsed, "lapsed"],
    ["On hold — payment risk", data.held || [], "held"],
  ];
  return (
    <section className="mb-16" data-testid="admin-trials">
      <p className="overline-label mb-8">Free-trial funnel & payment health</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {groups.map(([label, rows, key]) => (
          <div key={key} className="bg-background p-8" data-testid={`trials-${key}`}>
            <p className={`font-display text-4xl mb-1 ${key === "held" && rows.length ? "text-accent" : ""}`}>{rows.length}</p>
            <p className="overline-label !text-[10px] mb-6">{label}</p>
            <div className="flex flex-col gap-4">
              {rows.slice(0, 8).map((u) => (
                <div key={u.id} className="text-sm" data-testid={`trial-row-${u.id}`}>
                  <p className="text-white/85">{u.company || u.name}</p>
                  <p className="font-grotesk text-[10px] uppercase tracking-[0.12em] text-white/45">
                    {key === "held"
                      ? `${u.plan_name || "No plan"} · ${u.role === "model" ? "talent" : "brand"} · ${u.manual ? "office hold" : "card declined"}${u.hold_reason ? ` · "${u.hold_reason}"` : ""}`
                      : `${key === "in-trial" && u.days_left !== undefined ? `${u.days_left} days left · ` : ""}${u.plan_name || "No plan"}${u.offer_claimed ? " · offer claimed" : ""}`}
                  </p>
                </div>
              ))}
              {rows.length === 0 && <p className="font-display italic text-lg text-white/35">No one here yet.</p>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
